import { useRef, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { usePermissions } from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { useImportLeads } from '../api/leads';
import { refusalMessageOf } from '../model/refusals';

/**
 * The JSON paste of SDD-013 §7.3, kept under "Erweitert" in the import wizard (SDD-020 §9): a JSON
 * list of `CreateLeadRequest`s under this agreement, pasted or uploaded. Dry run FIRST — the result
 * table shows what each row would do — then the run. `on_error` and `register_partners` are the
 * request's own knobs; registering needs the Partner domain's key, so the option is gated like the
 * action is. Since SDD-020 the import also runs as a job; this path shows the SDD-013 record.
 */
export function JsonPasteImport({
  parentPolicyCode,
}: {
  readonly parentPolicyCode: string;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const { can } = usePermissions();
  const importLeads = useImportLeads();
  const [input, setInput] = useState('');
  const [onError, setOnError] = useState<'ABORT' | 'SKIP'>('SKIP');
  const [registerPartners, setRegisterPartners] = useState(false);
  const [batchKey, setBatchKey] = useState('');
  const [parseError, setParseError] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const job = importLeads.data?.job;

  const parseItems = (): InstanceApi.CreateLeadRequest[] | null => {
    try {
      const parsed: unknown = JSON.parse(input);
      if (!Array.isArray(parsed)) return null;
      return parsed as InstanceApi.CreateLeadRequest[];
    } catch {
      return null;
    }
  };

  const run = (asDryRun: boolean): void => {
    const items = parseItems();
    if (items === null) {
      setParseError(true);
      return;
    }
    setParseError(false);
    importLeads.mutate({
      parentPolicyCode,
      dryRun: asDryRun,
      onError,
      registerPartners,
      // The batch's name is its idempotency key (§7.3): the same name returns the recorded job
      // instead of a second import. Unnamed batches get a one-off key; a dry run writes no job.
      idempotencyKey: asDryRun
        ? ''
        : batchKey.trim() === ''
          ? `${parentPolicyCode}-${String(Date.now())}`
          : batchKey.trim(),
      items,
    });
  };

  return (
    <Stack spacing={2} sx={{ pt: 1 }} data-testid="json-paste">
      <Typography variant="body2" color="text.secondary">
        {t('leads.import.description')}
      </Typography>

      <TextField
        label={t('leads.import.input')}
        value={input}
        multiline
        minRows={6}
        fullWidth
        size="small"
        helperText={parseError ? t('leads.import.invalidJson') : t('leads.import.inputHint')}
        error={parseError}
        onChange={(event) => {
          setInput(event.target.value);
        }}
        slotProps={{ htmlInput: { style: { fontFamily: 'monospace' }, spellCheck: false } }}
      />
      <Button
        size="small"
        variant="text"
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => {
          fileInput.current?.click();
        }}
      >
        {t('leads.import.file')}
      </Button>
      <input
        type="file"
        accept="application/json,.json"
        hidden
        ref={fileInput}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file === undefined) return;
          void file.text().then(setInput);
        }}
      />

      <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label={t('leads.import.batchKey')}
          value={batchKey}
          helperText={t('leads.import.batchKeyHint')}
          sx={{ minWidth: 260 }}
          onChange={(event) => {
            setBatchKey(event.target.value);
          }}
          slotProps={{ htmlInput: { spellCheck: false } }}
        />
        <TextField
          select
          size="small"
          label={t('leads.import.onError')}
          value={onError}
          sx={{ minWidth: 220 }}
          onChange={(event) => {
            setOnError(event.target.value === 'ABORT' ? 'ABORT' : 'SKIP');
          }}
        >
          <MenuItem value="SKIP">{t('leads.import.skip')}</MenuItem>
          <MenuItem value="ABORT">{t('leads.import.abort')}</MenuItem>
        </TextField>
        {can(p.partners.create) && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={registerPartners}
                onChange={(event) => {
                  setRegisterPartners(event.target.checked);
                }}
              />
            }
            label={t('leads.import.registerPartners')}
          />
        )}
      </Stack>

      {importLeads.isError && (
        <Alert severity="error" data-testid="import-refusal">
          {refusalMessageOf(importLeads.error)}
        </Alert>
      )}

      {job !== undefined && (
        <Stack spacing={1.5} data-testid="import-result">
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="subtitle2">
              {job.dryRun === true
                ? t('leads.import.check')
                : t('leads.import.job', { jobId: job.jobId ?? '' })}
            </Typography>
            <Chip
              size="small"
              color={job.state === 'COMPLETED' ? 'success' : 'warning'}
              variant="outlined"
              label={t(`leads.import.state.${job.state ?? ''}` as never, {
                defaultValue: job.state ?? '',
              })}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('leads.import.counts', {
              items: job.counts?.items ?? 0,
              created: job.counts?.created ?? 0,
              valid: job.counts?.valid ?? 0,
              skipped: job.counts?.skipped ?? 0,
              failed: job.counts?.failed ?? 0,
            })}
          </Typography>
          {job.note !== undefined && job.note !== '' && <Alert severity="info">{job.note}</Alert>}
          <Table size="small" aria-label={t('leads.import.results')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('leads.import.row')}</TableCell>
                <TableCell>{t('leads.import.outcome')}</TableCell>
                <TableCell>{t('leads.import.leadCode')}</TableCell>
                <TableCell>{t('leads.import.reason')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(job.items ?? []).map((item) => (
                <TableRow key={item.row ?? 0} data-testid="import-row">
                  <TableCell>{item.row ?? 0}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={
                        item.outcome === 'CREATED' || item.outcome === 'VALID'
                          ? 'success'
                          : 'warning'
                      }
                      label={t(`leads.import.outcomes.${item.outcome ?? ''}` as never, {
                        defaultValue: item.outcome ?? '',
                      })}
                    />
                  </TableCell>
                  <TableCell>
                    {item.leadCode === undefined || item.leadCode === '' ? '—' : item.leadCode}
                  </TableCell>
                  <TableCell>
                    {item.reason === undefined ? (
                      '—'
                    ) : (
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{item.reason.message}</Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {item.reason.code} · {item.reason.resource}
                        </Typography>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      )}
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          disabled={importLeads.isPending || input.trim() === ''}
          onClick={() => {
            run(true);
          }}
        >
          {t('leads.import.check')}
        </Button>
        <Button
          variant="contained"
          disabled={
            importLeads.isPending ||
            input.trim() === '' ||
            (job !== undefined && job.dryRun !== true)
          }
          onClick={() => {
            run(false);
          }}
        >
          {t('leads.import.run')}
        </Button>
      </Stack>
    </Stack>
  );
}
